import axios from "axios";
import { getApiDomain, hostname } from "../../utils";

/**
 * Install Google Drive using Composio OAuth.
 */
export async function installGoogleDriveComposio(
  companyId: string,
  instanceName: string,
  composioConnectedAccountId: string,
  googleDriveFolderId: string,
  destinationPath: string,
  syncFrequency: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/google_docs/composio/install`,
    {
      companyId,
      instanceName,
      composioConnectedAccountId,
      googleDriveFolderId,
      destinationPath,
      syncFrequency,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Atlassian (Confluence) integration using API token.
 */
export async function installAtlassian(
  companyId: string,
  instanceName: string,
  destinationPath: string,
  domain: string,
  email: string,
  apiToken: string,
  syncFrequency: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/atlassian/install`,
    {
      companyId,
      instanceName,
      destinationPath,
      parameters: {
        domain,
        email,
        apiToken,
      },
      syncFrequency,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Notion integration using Internal Integration Secret.
 */
export async function installNotion(
  companyId: string,
  instanceName: string,
  destinationPath: string,
  integrationSecret: string,
  syncFrequency: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/notion/install`,
    {
      companyId,
      instanceName,
      destinationPath,
      parameters: {
        integrationSecret,
      },
      syncFrequency,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Discord integration.
 * Returns a redirect URL to complete install.
 */
export async function installDiscord(
  companyId: string,
  getToken: Function,
  instanceName: string,
  destinationFolder: string
): Promise<string> {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/discord/install`,
    {
      companyId,
      redirectUrl: window.location.href,
      instanceName,
      destinationFolder,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data.redirect_url;
}

/**
 * Install Microsoft Teams integration.
 * Returns a redirect URL to complete bot installation.
 */
export async function installTeams(
  companyId: string,
  botId: string,
  getToken: Function
): Promise<string> {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/teams/install`,
    {
      companyId,
      botId,
      redirectUrl: window.location.href,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data.redirect_url;
}

/**
 * Create or retrieve API key for Zapier integration.
 */
export async function getApiKey(
  integrationName: string,
  companyId: string,
  getToken: Function
): Promise<string> {
  const tok = await getToken();
  const res = await axios.put(
    `${hostname}/api_keys`,
    { name: integrationName, companyId },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data.apiKey;
}

/**
 * Install WordPress integration.
 */
export async function installWordpress(
  companyId: string,
  integrationName: string,
  blogDomain: string,
  blogUsername: string,
  blogPassword: string,
  destinationFolder: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.put(
    `${hostname}/integrations/wordpress/install`,
    {
      name: integrationName,
      companyId,
      domain: blogDomain,
      username: blogUsername,
      password: blogPassword,
      destinationFolder,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install AWS integration.
 */
export async function installAws(
  companyId: string,
  integrationName: string,
  accessKeyId: string,
  secretAccessKey: string,
  region: string,
  instanceName: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.put(
    `${hostname}/integrations/aws/install`,
    {
      name: integrationName,
      companyId,
      accessKeyId,
      secretAccessKey,
      region,
      instanceName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install SendGrid integration.
 */
export async function installSendgrid(
  companyId: string,
  integrationName: string,
  sendgridApiKey: string,
  instanceName: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.put(
    `${hostname}/integrations/sendgrid/install`,
    {
      name: integrationName,
      companyId,
      sendgridApiKey,
      instanceName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install HeyGen integration.
 */
export async function installHeygen(
  companyId: string,
  integrationName: string,
  apiKey: string,
  instanceName: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.put(
    `${hostname}/integrations/heygen/install`,
    {
      name: integrationName,
      companyId,
      apiKey,
      instanceName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install n8n integration.
 */
export async function installN8n(
  companyId: string,
  integrationName: string,
  apiKey: string,
  instanceUrl: string,
  instanceName: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/n8n/setup`,
    {
      companyId,
      apiKey,
      instanceUrl,
      instanceName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Test n8n integration connection.
 */
export async function testN8n(
  companyId: string,
  instanceName: string,
  getToken: Function
): Promise<any> {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/n8n/test`,
    {
      companyId,
      instanceName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data;
}

/**
 * Generate a Slack install URL based on environment.
 */
export function getSlackInstallUrl(userId: string, companyId: string): string {
  return `${getApiDomain()}/integrations/slack/install?state=${userId}^${companyId}`;
}

/**
 * Install LinkedIn integration via OAuth
 */
export async function installLinkedIn(
  companyId: string,
  getToken: Function,
  instanceName: string,
  clientId: string,
  clientSecret: string
): Promise<string> {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/linkedin/install`,
    {
      companyId,
      instanceName,
      clientId,
      clientSecret,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data.auth_url;
}

/**
 * Install Meta integration with access token
 */
export async function installMeta(
  companyId: string,
  getToken: Function,
  instanceName: string,
  accessToken: string,
  pageId: string
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/meta/install`,
    {
      companyId,
      instanceName,
      accessToken,
      pageId,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

// Keep the old function name for backwards compatibility
export const installFacebook = installMeta;

/**
 * Install website crawler integration
 */
export async function installWebsite(
  instanceName: string,
  destinationPath: string,
  parameters: {
    url: string;
    maxPages: number;
    maxDepth: number;
  },
  companyId: string,
  getToken: Function,
  syncFrequency: string = "manual"
) {
  const tok = await getToken();
  const response = await axios.post(
    `${hostname}/integrations/website/install`,
    {
      companyId,
      instanceName,
      destinationPath,
      parameters,
      syncFrequency,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return response.data;
}

/**
 * Install Twilio integration.
 */
export async function installTwilio(
  companyId: string,
  integrationName: string,
  accountSid: string,
  authToken: string,
  instanceName: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.put(
    `${hostname}/integrations/twilio/install`,
    {
      name: integrationName,
      companyId,
      accountSid,
      authToken,
      instanceName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install IMAP integration.
 */
export async function installImap(
  companyId: string,
  integrationName: string,
  imapConfig: {
    imapHost: string;
    imapPort: number;
    smtpHost: string;
    smtpPort: number;
    email: string;
    password: string;
    useSsl: boolean;
    useTls: boolean;
    folder: string;
  },
  instanceName: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/imap/inboxes`,
    {
      name: integrationName,
      companyId,
      ...imapConfig,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install MCP server integration.
 */
export async function installMcp(
  companyId: string,
  serverData: {
    serverName: string;
    serverType: 'http' | 'stdio';
    serverUrl?: string;
    command?: string;
    args?: string[];
    apiKey?: string;
    headers?: Record<string, string>;
    useOAuth?: boolean;  // OAuth toggle from form
    oauth_tokens?: any;  // OAuth tokens from successful test connection
    oauth_client_info?: any;  // OAuth client info for token refresh
  },
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  const payload = {
    companyId,
    ...serverData,
  };
  console.log("Installing MCP with payload:", payload);
  await axios.post(
    `${hostname}/integrations/mcp/install`,
    payload,
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Test MCP server connection.
 */
export async function testMcpServer(
  integrationId: string,
  companyId: string,
  getToken: Function
): Promise<{ success: boolean; tools?: any[]; error?: string }> {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/mcp/test/${integrationId}`,
    {
      companyId,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data;
}

/**
 * Install Office 365 integration with OAuth tokens.
 */
export async function installOffice365(
  companyId: string,
  instanceName: string,
  email: string,
  oauthTokens: any,
  getToken: Function,
  useGraphApi: boolean = false
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/office365/install`,
    {
      companyId,
      instanceName,
      email,
      parameters: {
        email,
        oauth_tokens: oauthTokens,
        // Standard Office 365 IMAP/SMTP settings
        imapHost: "outlook.office365.com",
        imapPort: 993,
        smtpHost: "smtp.office365.com",
        smtpPort: 587,
        useSsl: true,
        useTls: true,
        useOAuth: true,
        useGraphApi,
        folder: "INBOX"
      },
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Gmail mailbox via Composio OAuth.
 * This creates both the integration and the Gmail inbox.
 */
export async function installGmail(
  companyId: string,
  inboxName: string,
  email: string,
  composioConnectedAccountId: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/gmail/inboxes`,
    {
      companyId,
      name: inboxName,
      email,
      composioConnectedAccountId,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install LinkedIn Login integration via live browser session.
 */
export async function installLinkedInLogin(
  companyId: string,
  integrationName: string,
  sessionId: string,
  email: string,
  getToken: Function,
  sessionData?: {
    contextId?: string;
    zipCode?: string;
    os?: string;
    fingerprint?: any;
    userAgent?: string;
    startUrl?: string;
  }
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/browser-sessions/save`,
    {
      sessionId,
      companyId,
      integrationType: 'linkedin_login',
      instanceName: integrationName,
      email,
      // Session data for multi-instance compatibility
      contextId: sessionData?.contextId,
      startUrl: sessionData?.startUrl || 'https://www.linkedin.com/login',
      zipCode: sessionData?.zipCode,
      os: sessionData?.os,
      fingerprint: sessionData?.fingerprint,
      userAgent: sessionData?.userAgent,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install generic Browser Session integration via live browser session.
 */
export async function installBrowserSession(
  companyId: string,
  integrationName: string,
  sessionId: string,
  url: string,
  getToken: Function,
  loginCredentials?: Array<{ domain?: string; credentials: Record<string, string> }>,
  loginInstructions?: string,
  sessionData?: {
    contextId?: string;
    zipCode?: string;
    os?: string;
    fingerprint?: any;
    userAgent?: string;
    startUrl?: string;
  }
): Promise<void> {
  const tok = await getToken();

  // Debug logging
  console.log("installBrowserSession called with:");
  console.log("  loginCredentials:", loginCredentials);
  console.log("  loginInstructions:", loginInstructions);
  console.log("  sessionData:", sessionData);

  await axios.post(
    `${hostname}/browser-sessions/save`,
    {
      sessionId,
      companyId,
      integrationType: 'browser_session',
      instanceName: integrationName,
      additionalParams: {
        url,
      },
      loginCredentials,
      loginInstructions,
      // Session data for multi-instance compatibility
      contextId: sessionData?.contextId,
      startUrl: sessionData?.startUrl || url,
      zipCode: sessionData?.zipCode,
      os: sessionData?.os,
      fingerprint: sessionData?.fingerprint,
      userAgent: sessionData?.userAgent,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Shopify integration with API credentials.
 */
export async function installShopify(
  companyId: string,
  instanceName: string,
  shopUrl: string,
  apiKey: string,
  apiSecret: string,
  apiVersion: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/shopify/install`,
    {
      companyId,
      instanceName,
      parameters: {
        shopUrl,
        apiKey,
        apiSecret,
        apiVersion,
      },
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Scheduling integration (MeetQuick, Calendly, etc.).
 */
export async function installScheduling(
  companyId: string,
  instanceName: string,
  scheduleUrl: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/scheduling/install`,
    {
      companyId,
      instanceName,
      parameters: {
        scheduleUrl,
      },
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install HubSpot CRM integration.
 */
export async function installHubspot(
  companyId: string,
  instanceName: string,
  accessToken: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/crm/integrations/hubspot`,
    {
      companyId,
      instanceName,
      accessToken,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install GoHighLevel CRM integration.
 */
export async function installGoHighLevel(
  companyId: string,
  instanceName: string,
  accessToken: string,
  locationId: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/crm/integrations/gohighlevel`,
    {
      companyId,
      instanceName,
      accessToken,
      locationId,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Apollo integration.
 */
export async function installApollo(
  companyId: string,
  instanceName: string,
  apiKey: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/apollo/install`,
    {
      companyId,
      instanceName,
      apiKey,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Findymail integration.
 */
export async function installFindymail(
  companyId: string,
  instanceName: string,
  apiKey: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/findymail/install`,
    {
      companyId,
      instanceName,
      apiKey,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Better Contact integration.
 */
export async function installBetterContact(
  companyId: string,
  instanceName: string,
  apiKey: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/bettercontact/install`,
    {
      companyId,
      instanceName,
      apiKey,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Prosp integration.
 */
export async function installProsp(
  companyId: string,
  instanceName: string,
  apiKey: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/prosp/install`,
    {
      companyId,
      instanceName,
      apiKey,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install HeyReach integration.
 */
export async function installHeyReach(
  companyId: string,
  instanceName: string,
  apiKey: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/heyreach/install`,
    {
      companyId,
      instanceName,
      apiKey,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Salesforge integration.
 */
export async function installSalesforge(
  companyId: string,
  instanceName: string,
  apiKey: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/salesforge/install`,
    {
      companyId,
      instanceName,
      apiKey,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install a Composio-based integration (Facebook, Instagram, LinkedIn, etc.)
 * This saves the connected account info from Composio to our database.
 */
export async function installComposioIntegration(
  companyId: string,
  instanceName: string,
  integrationType: string,
  composioConnectedAccountId: string,
  composioToolkit: string,
  getToken: Function,
  selectedPage?: {
    id: string;
    name: string;
    category?: string;
    picture?: string;
    pageAccessToken?: string;
    username?: string;
    followers?: number;
    facebookPageId?: string;
    facebookPageName?: string;
    // LinkedIn-specific fields
    type?: "personal" | "organization";
    authorUrn?: string;
    email?: string;
    vanityName?: string;
    industry?: string;
  },
  manualAuthorUrn?: string
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/composio/install`,
    {
      companyId,
      instanceName,
      integrationType,
      composioConnectedAccountId,
      composioToolkit,
      selectedPage,
      manualAuthorUrn,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Webflow CMS integration.
 * Uses API token authentication with site and collection selection.
 */
export async function installWebflow(
  companyId: string,
  instanceName: string,
  apiToken: string,
  siteId: string,
  siteName: string,
  collectionId: string,
  collectionName: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/webflow/install`,
    {
      companyId,
      instanceName,
      apiToken,
      siteId,
      siteName,
      collectionId,
      collectionName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Thanks.io integration.
 */
export async function installThanksio(
  companyId: string,
  instanceName: string,
  apiKey: string,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/thanksio/install`,
    {
      companyId,
      instanceName,
      apiKey,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Install Meta Ads integration using System User Access Token.
 */
export async function installMetaAds(
  companyId: string,
  instanceName: string,
  accessToken: string,
  userName: string,
  adAccountCount: number,
  getToken: Function
): Promise<void> {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/meta-ads/install`,
    {
      companyId,
      instanceName,
      accessToken,
      userName,
      adAccountCount,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

